import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs";
import { useFeatureEntitlements } from "@frontegg/react";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { Accordion } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import Summary, { type SummaryItem } from "components/common/Summary";
import CardShippingDetails, {
  type CardShipping,
} from "components/common/cards/CardShippingDetails";
import CardType from "enums/CardType";
import CommonDateFormat from "enums/CommonDateFormat";

interface CardDates {
  card_activation_date?: string | number | null;
  card_expiration_date?: string | number | null;
  card_cancellation_date?: string | number | null;
}

interface CardDetailsPanelProps {
  card?: {
    nickname?: string | null;
    cardProgram?: { nickname?: string | null } | null;
    type?: string | null;
    shipping?: CardShipping | null;
    shippingSpeed?: string | null;
    customFields?: unknown;
  } | null;
  policy?: CardDates | null;
  pendingPolicy?: CardDates | null;
  hasPendingChange?: boolean;
  requestTypeLabel?: React.ReactNode;
}

const asDate = (value?: string | number | null) =>
  value && dayjs(value).isValid()
    ? dayjs(value).format(CommonDateFormat.MMDDYYYY)
    : "";

export const CardDetailsTab: React.FC<CardDetailsPanelProps> = ({
  card,
  policy,
  pendingPolicy,
  hasPendingChange = false,
  requestTypeLabel,
}) => {
  const { t } = useTranslation();
  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  const { transformCustomFieldsToRecord } = useCustomFieldRenderer({});
  const { filteredCustomFields: customFields } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.CARD,
    customFields: transformCustomFieldsToRecord(card?.customFields || []),
  });

  const cancelationDate = policy?.card_cancellation_date;
  const pendingCancelationDate = pendingPolicy?.card_cancellation_date;

  // Only a real change is worth striking the current value through: the same
  // date, or neither set, is not one.
  const pendingCancelation =
    hasPendingChange &&
    !(pendingCancelationDate == null && cancelationDate == null)
      ? pendingCancelationDate == null
        ? t("No cancelation date")
        : asDate(pendingCancelationDate) !== asDate(cancelationDate)
          ? asDate(pendingCancelationDate)
          : undefined
      : undefined;

  const detailItems: SummaryItem[] = [
    { title: t("Nickname"), value: card?.nickname },
    { title: t("Card program"), value: card?.cardProgram?.nickname },
    { title: t("Request Type"), value: requestTypeLabel },
    ...(card?.type !== CardType.EXTERNAL
      ? [
          {
            title: t("Activation Date"),
            value: asDate(policy?.card_activation_date),
          },
          {
            title: t("Expiration Date"),
            value: asDate(policy?.card_expiration_date),
            tooltip: t("VisaExpirationDateTooltip"),
          },
          ...(cancelationDate ||
          (hasPendingChange && pendingCancelationDate !== undefined)
            ? [
                {
                  title: t("Cancelation Date"),
                  value: asDate(cancelationDate) || t("No cancelation date"),
                  pendingValue: pendingCancelation,
                  tooltip: t(
                    "Outpave will automatically cancel your card on this date",
                  ),
                },
              ]
            : []),
        ]
      : []),
    ...(featureCustomFields && customFields.length > 0
      ? customFields.map((field) => ({
          title: field.label,
          value: field.value,
        }))
      : []),
  ];

  return (
    <Stack gap="var(--space-6)">
      <Summary disableContainer items={detailItems} />

      {card?.type === CardType.PHYSICAL && card?.shipping ? (
        <Accordion title={t("Shipping Details")}>
          <CardShippingDetails
            shipping={card.shipping}
            shippingSpeed={card.shippingSpeed}
          />
        </Accordion>
      ) : null}
    </Stack>
  );
};

export default CardDetailsTab;
